import { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { intakes } from '@/api/endpoints';
import type { Intake } from '@/types';
import { Icon } from '@/components/Icon';
import { useSortable, SortHeader } from '@/components/SortableTable';
import { Pagination, usePaginated } from '@/components/Pagination';

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Wszystkie' },
  { value: 'ACTIVE', label: 'Aktywne' },
  { value: 'ARCHIVED', label: 'Archiwalne' },
];

const DECYDENCI: { value: string; label: string }[] = [
  { value: '', label: 'Wszyscy' },
  { value: 'REGIONAL', label: 'Marszałek' },
  { value: 'DISTRICT', label: 'Starosta' },
];

const RODZAJE: { value: string; label: string }[] = [
  { value: '', label: 'Wszystkie' },
  { value: 'NEW', label: 'Nowe' },
  { value: 'EXISTING', label: 'Istniejące' },
];

export function IntakesList() {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') ?? '';
  const authority = params.get('authority') ?? '';
  const kind = params.get('kind') ?? '';
  const [rows, setRows] = useState<Intake[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    setRows(null);
    setErr(null);
    intakes.list(filter || undefined, authority || undefined)
      .then(setRows)
      .catch(e => setErr(String(e)));
  }, [filter, authority]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  // Rodzaj (nowe/istniejące) + szukajka filtrujemy po stronie klienta — lista jest już wczytana,
  // więc zmiana jest natychmiastowa bez dodatkowego zapytania.
  const needle = q.trim().toLowerCase();
  const visible = rows ? rows.filter(u =>
    (!kind || u.kind === kind) &&
    (!needle
      || u.name.toLowerCase().includes(needle)
      || u.number.toLowerCase().includes(needle)
      || u.client.name.toLowerCase().includes(needle)
      || (u.town ?? '').toLowerCase().includes(needle))
  ) : null;
  const maxQ = visible?.reduce((m, u) => Math.max(m, u.qM3h ?? 0), 0) ?? 1;

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Ujęcia</h1>
          <div className="text-ink-400 text-[13px]">Lista ujęć wody podziemnej</div>
        </div>
        <div className="flex gap-2">
          <NavLink to="/mapy" className="btn btn-secondary btn-sm"><Icon name="map" className="w-3.5 h-3.5"/> Widok mapy</NavLink>
          <NavLink to="/intake-wizard" className="btn btn-primary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/> Kreator ujęcia</NavLink>
        </div>
      </header>

      <div className="card">
        <div className="card-head">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 bg-ink-50 p-1 rounded-md">
              {FILTERS.map(f => (
                <button
                  key={f.value || 'all'}
                  onClick={() => setParam('filter', f.value)}
                  className={'btn btn-sm ' + (filter === f.value ? 'btn-primary' : 'btn-ghost')}
                >{f.label}</button>
              ))}
            </div>
            <div className="flex gap-1 bg-ink-50 p-1 rounded-md ml-3">
              {DECYDENCI.map(d => (
                <button
                  key={d.value || 'all'}
                  onClick={() => setParam('authority', d.value)}
                  className={'btn btn-sm ' + (authority === d.value ? 'btn-primary' : 'btn-ghost')}
                >{d.label}</button>
              ))}
            </div>
            <div className="flex gap-1 bg-ink-50 p-1 rounded-md ml-3">
              {RODZAJE.map(r => (
                <button
                  key={r.value || 'all'}
                  onClick={() => setParam('kind', r.value)}
                  className={'btn btn-sm ' + (kind === r.value ? 'btn-primary' : 'btn-ghost')}
                >{r.label}</button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-400"/>
                <input value={q} onChange={e => setQ(e.target.value)}
                       placeholder="Szukaj: nazwa, numer, klient, miejscowość…"
                       className="input !py-1.5 !pl-8 !pr-2 text-[13px] w-72"/>
              </div>
              {visible && <span className="text-[12px] text-ink-400 tabular-nums whitespace-nowrap">Wyników: {visible.length}</span>}
            </div>
          </div>
        </div>
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!visible && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {visible && visible.length === 0 && <div className="text-ink-400 p-4">Brak ujęć do wyświetlenia.</div>}
          {visible && visible.length > 0 && <IntakesTable rows={visible} maxQ={maxQ} onRowClick={id => nav(`/intakes/${id}`)}/>}
        </div>
      </div>
    </div>
  );
}

type SortKey = 'number' | 'name' | 'qM3h' | 'authority' | 'boreholes' | 'studies' | 'validFrom' | 'createdAt';

function IntakesTable({ rows, maxQ, onRowClick }: { rows: Intake[]; maxQ: number; onRowClick: (id: number) => void }) {
  const accessor = useCallback((u: Intake, k: SortKey) => {
    switch (k) {
      case 'number': return u.number;
      case 'name': return u.name;
      case 'qM3h': return Number(u.qM3h ?? 0);
      case 'authority': return u.authorityName;
      case 'boreholes': return u.boreholes.length;
      case 'studies': return u.studies.length;
      case 'validFrom': return u.validFrom ? new Date(u.validFrom) : null;
      case 'createdAt': return u.createdAt ? new Date(u.createdAt) : null;
    }
  }, []);
  const { sorted, sort, onHeaderClick } = useSortable<Intake, SortKey>(rows, 'createdAt', 'desc', accessor);
  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(sorted, { storageKey: 'intakes' });

  return (
    <>
    <table className="table-hw">
      <thead>
        <tr>
          <SortHeader<SortKey> label="Numer"        sortKey="number"     current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Nazwa ujęcia" sortKey="name"       current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Wydajność Q"  sortKey="qM3h"       current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Otwory"       sortKey="boreholes"  current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Opracowania"  sortKey="studies"    current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Od"           sortKey="validFrom"  current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Utworzone"    sortKey="createdAt"  current={sort} onClick={onHeaderClick}/>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {pagedItems.map(u => {
          const pct = Math.round(((u.qM3h ?? 0) / maxQ) * 100);
          return (
            <tr key={u.id} onClick={() => onRowClick(u.id)} className="cursor-pointer hover:bg-ink-50">
              <td className="font-mono text-[12px] text-ink-500">{u.number}</td>
              <td>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-ink-900">{u.name}</span>
                  <span className={'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ' +
                    (u.kind === 'EXISTING' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700')}>
                    {u.kind === 'EXISTING' ? 'Istniejące' : 'Nowe'}
                  </span>
                </div>
                <div className="text-[11px] text-ink-400">{u.client.name}</div>
              </td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="font-mono tabular-nums text-ink-800 text-[12px] w-16">{u.qM3h ?? '—'} m³/h</div>
                  <div className="relative w-24 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-water-500" style={{ width: pct + '%' }}/>
                  </div>
                </div>
              </td>
              <td className="font-mono tabular-nums text-ink-700">{u.boreholes.length}</td>
              <td className="font-mono tabular-nums text-ink-700">{u.studies.length}</td>
              <td className="text-ink-500 text-[12px]">{u.validFrom ?? '—'}</td>
              <td className="text-ink-500 text-[12px] font-mono whitespace-nowrap">{fmtDateTime(u.createdAt)}</td>
              <td><Icon name="chevron" className="text-ink-300"/></td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <Pagination total={total} page={page} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={setPageSize}/>
    </>
  );
}
